import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Sparkles,
  RotateCcw,
  BookOpen,
  Layers,
  Megaphone,
  Loader2,
  ArrowRight,
  Quote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Eyebrow } from "@/components/landing/Eyebrow";
import { Typewriter } from "@/components/landing/Motion";

/**
 * Two demo cases. Each case demonstrates how our AI + positioning framework
 * turns an industry trigger into a full narrative script
 * with three blocks: STORY → FRAMEWORK → CTA.
 */
const INDUSTRIES = ["AI 導師", "理財顧問"];

const SCRIPTS = {
  "AI 導師": {
    tag: "AI 教育 / 專家品牌",
    hook: "你會什麼，已經不值錢了。",
    subHook:
      "不是因為 AI 比你厲害，是「會什麼」這個公式本身，已經死了。",
    story: [
      "我一個朋友，做了十五年設計。上個月收到客戶一條訊息：「不好意思，我找了個新人，他用 AI 做，三天交稿，報價只有你一半。」",
      "他呆了一下，回：「他做得出我那種質感嗎？」客戶想了很久，回了一句：「老實說⋯⋯差不多。」",
      "他打給我只說一句話：「做了十五年，一夜之間不值錢了。」",
    ],
    frameworkIntro: [
      "我跟他說，你搞錯了一件事。讓你不值錢的不是 AI，是你還在用「我會什麼」來定價。",
      "以前你值多少錢，看你會什麼、做了多久，這兩樣加起來就是你的身價。這個公式撐了一百年。但 AI 一出來它整個崩掉——不是因為 AI 把你取代，是因為 AI 讓「會」這個字本身變得沒意義。",
    ],
    frameworkQuestion:
      "那現在按什麼定價？你自己想一下，提到某個領域，你腦子裡會冒出哪幾個名字？那幾個人，專業不一定最強，作品也不一定最好，但生意都往他們那跑，報價還特別貴。",
    frameworkInsight:
      "這就是新的定價單位——不是你技能多稀缺，是你在多少人腦子裡，佔了一個位置。",
    frameworkPrompt:
      "在你做的那個領域，列 10 個可能的客戶，問自己：提到這件事，有幾個人會第一個想到你？",
    frameworkBullets: [
      { trigger: "0 個", tone: "down", result: "不管你多專業，你的定價都在崩塌" },
      { trigger: "1–2 個", tone: "mid", result: "有位置，但那個位置很小" },
      { trigger: "10 個以上", tone: "up", result: "AI 再強，動不到你" },
    ],
    ctaLead:
      "那個十五年的設計師後來問：「我這十五年——白做了？」我說：不是白做。是你這十五年只做了一半。你練到了「會」，但沒練到「被想起」。前面一半，AI 一年就追平；後面一半，它一百年都追不上。",
    ctaPunch: "未來值錢的人，不會是會最多東西的那個。",
    ctaPunchHi: "是最多人想起的那個。",
  },

  "理財顧問": {
    tag: "財富 / 事業結構",
    hook: "賺越多錢，選擇越少。",
    subHook: "你擁有事業，還是事業擁有你？",
    story: [
      "我有個律師朋友，月入三十萬。上個月他太太生第二胎，他想請兩個星期假陪月。他拿出行事曆，發現一格都空不出來。",
      "客戶等他簽文件，團隊等他開會，兩棟樓的房貸等他供。他坐在辦公室跟我說：「我做了十二年律師，建了一個離不開我的事業——其實是它擁有了我。」",
      "同一個星期，我另一個朋友，收入只有他三分之一，直接關手機，消失兩星期，去冰島看極光。",
      "這兩個人的差別不是錢，是一樣更根本的東西——「選擇權」。",
    ],
    frameworkIntro: [
      "大部分人一輩子追的那個叫「事業」，它其實只是手段，真正的目的是選擇權。我們拼到一半把手段當成目的，結果事業越大、選擇權越少。",
    ],
    frameworkQuestion:
      "你能不能明天跟最大客戶說「不」？能不能下個月消失兩個星期？能不能突然換一條完全不同的跑道？",
    frameworkInsight:
      "如果全部都不行，你建的不是事業，是一個需要你永遠在場的東西。",
    frameworkPrompt: "差別不在於你多努力，在於結構——",
    frameworkBullets: [
      { trigger: "接一個大單，綁你三年", tone: "down", result: "選擇權減少" },
      { trigger: "請一個能獨當一面的人", tone: "up", result: "選擇權增加" },
      { trigger: "收入全押在一個客戶身上", tone: "down", result: "選擇權減少" },
      { trigger: "做一件你睡著還在賺錢的事", tone: "up", result: "選擇權增加" },
    ],
    ctaLead:
      "那個律師朋友最後跟我說：「我以為我在為自由工作，回頭看才發現，這十二年，每一步都在把自己關得更緊。」",
    ctaPunch: "事業做大，不是成功。",
    ctaPunchHi: "能隨時離開它——才是。",
  },
};

// Animated thinking dots "● ● ●"
const ThinkingDots = () => (
  <span className="inline-flex gap-1 ml-1 align-middle" data-testid="ai-demo-thinking-dots">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--lime))]"
        animate={{ opacity: [0.25, 1, 0.25] }}
        transition={{
          duration: 0.9,
          repeat: Infinity,
          delay: i * 0.15,
        }}
      />
    ))}
  </span>
);

export const AIDemo = ({ onCtaClick }) => {
  const [industry, setIndustry] = useState(INDUSTRIES[0]);
  const [phase, setPhase] = useState("idle"); // idle | thinking | result
  const [genKey, setGenKey] = useState(0); // force remount for typewriter restart
  const outputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  const handleGenerate = () => {
    setPhase("thinking");
    timerRef.current = setTimeout(() => {
      setPhase("result");
      setGenKey((k) => k + 1);
      // scroll output into view on mobile
      setTimeout(() => {
        if (outputRef.current && window.innerWidth < 768) {
          outputRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }, 120);
    }, 1400);
  };

  const handleReset = () => {
    setPhase("idle");
    setGenKey((k) => k + 1);
  };

  const data = SCRIPTS[industry] || SCRIPTS[INDUSTRIES[0]];

  return (
    <section
      id="ai-demo"
      data-testid="ai-demo-section"
      className="relative bg-ink-warm py-14 sm:py-24 lg:py-28"
    >
      <div className="absolute top-0 left-0 right-0 hairline-h" aria-hidden />
      {/* Subtle lime spotlight */}
      <div className="absolute inset-0 pointer-events-none spotlight-small" aria-hidden />

      <div className="relative container px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="text-center max-w-3xl mx-auto">
          <div className="flex justify-center">
            <Eyebrow label="LIVE DEMO" sub="AI SCRIPT" />
          </div>
          <h2 className="font-serif-tc mt-5 sm:mt-7 text-[30px] leading-[1.15] sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em]">
            親身體驗 AI <span className="lime-keyword">30 秒生成劇本</span>
          </h2>
          <p className="mt-4 sm:mt-6 text-[14px] sm:text-base leading-relaxed text-[hsl(var(--muted-foreground))]">
            分享會現場會用你的行業即場示範，以下為預錄範例。
            選擇一個案例 → 點擊生成，看看 AI 如何把一句痛點，展開成一條能引爆私訊的故事劇本。
          </p>
        </div>

        {/* Terminal-style panel */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 sm:mt-14 rounded-[24px] overflow-hidden border border-[rgba(255,255,255,0.08)] bg-gradient-to-b from-[rgba(255,255,255,0.04)] to-[rgba(255,255,255,0.01)] shadow-[0_40px_100px_-30px_rgba(212,180,106,0.2)]"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-5 sm:px-6 py-3 border-b border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)]">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--lime))]" />
              <span className="ml-3 font-num text-[11px] text-[hsl(var(--muted-foreground))] tracking-wider">
                reelscript.ai · AI SCRIPT GENERATOR
              </span>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-[hsl(var(--lime))]">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--lime))] animate-pulse" />
              ONLINE
            </div>
          </div>

          <div className="p-5 sm:p-7 lg:p-8">
            {/* INPUT */}
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--lime))] mb-3">
                <span className="font-num">&gt;</span> INPUT · 選擇案例
              </div>
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map((it) => {
                  const active = industry === it;
                  return (
                    <button
                      key={it}
                      data-testid={`ai-demo-industry-${it}`}
                      onClick={() => setIndustry(it)}
                      disabled={phase === "thinking"}
                      className={`text-[12px] sm:text-[13px] h-9 px-4 rounded-full border transition-[background-color,border-color,color] duration-200 ${
                        active
                          ? "bg-[hsl(var(--lime))] text-[#050505] border-[hsl(var(--lime))] font-semibold"
                          : "bg-transparent text-[hsl(var(--foreground))]/80 border-[rgba(255,255,255,0.14)] hover:border-[rgba(212,180,106,0.5)] hover:text-[hsl(var(--lime))]"
                      } disabled:opacity-50`}
                    >
                      {it}
                    </button>
                  );
                })}
              </div>

              {/* Generate button */}
              <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Button
                  data-testid="ai-demo-generate-button"
                  onClick={handleGenerate}
                  disabled={phase === "thinking"}
                  size="lg"
                  className="btn-lime h-12 px-6 text-[15px] sm:w-auto w-full"
                >
                  {phase === "thinking" ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      AI 分析中
                    </>
                  ) : phase === "result" ? (
                    <>
                      <RotateCcw className="w-4 h-4 mr-1.5" />
                      重新生成
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-1.5" />
                      生成爆款劇本
                    </>
                  )}
                </Button>
                {phase === "result" && (
                  <button
                    data-testid="ai-demo-reset-button"
                    onClick={handleReset}
                    className="text-[12px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                  >
                    清空結果
                  </button>
                )}
              </div>
            </div>

            {/* Separator */}
            <div className="my-7 sm:my-8 h-px bg-gradient-to-r from-transparent via-[rgba(255,255,255,0.1)] to-transparent" />

            {/* OUTPUT */}
            <div ref={outputRef} className="min-h-[320px]">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--lime))] mb-4">
                <span className="font-num">&gt;</span> OUTPUT · AI 生成結果
                {phase === "thinking" && (
                  <span className="ml-1.5">
                    <ThinkingDots />
                  </span>
                )}
              </div>

              <AnimatePresence mode="wait">
                {phase === "idle" && (
                  <motion.div
                    key="idle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="rounded-xl border border-dashed border-[rgba(255,255,255,0.1)] p-6 sm:p-8 text-center text-[13px] sm:text-sm text-[hsl(var(--muted-foreground))]"
                    data-testid="ai-demo-idle-state"
                  >
                    <Sparkles className="w-5 h-5 mx-auto mb-2 text-[hsl(var(--lime))]/60" />
                    選擇案例並點擊「生成爆款劇本」以查看 AI 輸出
                  </motion.div>
                )}

                {phase === "thinking" && (
                  <motion.div
                    key="thinking"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-2.5"
                    aria-live="polite"
                    data-testid="ai-demo-thinking-state"
                  >
                    {[
                      "解析案例主題 · 檢索痛點資料庫⋯⋯",
                      "比對 3,000+ 爆款敘事骨架⋯⋯",
                      "組合 Hook × STORY × FRAMEWORK × CTA⋯⋯",
                    ].map((line, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -6 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.28 }}
                        className="font-num text-[12px] sm:text-[13px] text-[hsl(var(--muted-foreground))] flex items-center gap-2"
                      >
                        <span className="text-[hsl(var(--lime))]">▸</span>
                        <span>{line}</span>
                      </motion.div>
                    ))}
                  </motion.div>
                )}

                {phase === "result" && (
                  <motion.div
                    key={`result-${genKey}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="space-y-5"
                    data-testid="ai-demo-result"
                  >
                    {/* HOOK — big thesis statement */}
                    <div className="rounded-xl p-5 sm:p-6 border border-[rgba(212,180,106,0.28)] bg-[rgba(212,180,106,0.05)]">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--lime))] mb-3">
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>爆款主題 · {data.tag}</span>
                      </div>
                      <div className="font-serif-tc text-[22px] sm:text-3xl lg:text-4xl leading-[1.25] text-[hsl(var(--foreground))] font-bold">
                        <Typewriter
                          text={data.hook}
                          speed={55}
                          startDelay={60}
                          cursor={false}
                        />
                      </div>
                      <div className="mt-3 text-[13px] sm:text-[14px] leading-relaxed text-[hsl(var(--muted-foreground))] italic">
                        <Typewriter
                          text={data.subHook}
                          speed={22}
                          startDelay={1200}
                          cursor={false}
                        />
                      </div>
                    </div>

                    {/* STORY — narrative block */}
                    <ResultBlock
                      icon={<BookOpen className="w-3.5 h-3.5" />}
                      label="STORY · 故事切入"
                    >
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.9, duration: 0.55 }}
                        className="space-y-3 text-[13px] sm:text-[14.5px] leading-[1.75] text-[hsl(var(--foreground))]/92"
                        data-testid="ai-demo-story"
                      >
                        {data.story.map((p, i) => (
                          <p key={i}>{p}</p>
                        ))}
                      </motion.div>
                    </ResultBlock>

                    {/* FRAMEWORK — the core breakdown */}
                    <ResultBlock
                      icon={<Layers className="w-3.5 h-3.5" />}
                      label="FRAMEWORK · 框架拆解"
                    >
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 2.3, duration: 0.55 }}
                        className="space-y-4 text-[13px] sm:text-[14.5px] leading-[1.75] text-[hsl(var(--foreground))]/92"
                        data-testid="ai-demo-framework"
                      >
                        {data.frameworkIntro.map((p, i) => (
                          <p key={`intro-${i}`}>{p}</p>
                        ))}
                        {data.frameworkQuestion && (
                          <p className="text-[hsl(var(--muted-foreground))] italic border-l-2 border-[rgba(212,180,106,0.35)] pl-4">
                            {data.frameworkQuestion}
                          </p>
                        )}
                        {data.frameworkInsight && (
                          <p className="text-[hsl(var(--lime))] font-semibold">
                            {data.frameworkInsight}
                          </p>
                        )}
                        {data.frameworkPrompt && (
                          <p className="text-[hsl(var(--foreground))]">
                            {data.frameworkPrompt}
                          </p>
                        )}

                        {/* Bullet matrix */}
                        <ul className="mt-2 space-y-2">
                          {data.frameworkBullets.map((b, i) => (
                            <motion.li
                              key={i}
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 2.6 + i * 0.14 }}
                              className="flex items-center gap-3 font-num text-[12.5px] sm:text-[13.5px]"
                              data-testid={`ai-demo-framework-bullet-${i}`}
                            >
                              <span className="px-2.5 py-1 rounded-md border border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.02)] text-[hsl(var(--foreground))] whitespace-nowrap">
                                {b.trigger}
                              </span>
                              <ArrowRight className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))] flex-shrink-0" />
                              <span
                                className={`${
                                  b.tone === "up"
                                    ? "text-[hsl(var(--lime))] font-semibold"
                                    : b.tone === "down"
                                    ? "text-[#E07A6B]"
                                    : "text-[hsl(var(--foreground))]/80"
                                }`}
                              >
                                {b.result}
                              </span>
                            </motion.li>
                          ))}
                        </ul>
                      </motion.div>
                    </ResultBlock>

                    {/* CTA — closing punchline */}
                    <ResultBlock
                      icon={<Megaphone className="w-3.5 h-3.5" />}
                      label="CTA · 收束金句"
                      accentRow
                    >
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 3.2, duration: 0.55 }}
                        className="space-y-4"
                        data-testid="ai-demo-cta"
                      >
                        <p className="text-[13px] sm:text-[14.5px] leading-[1.75] text-[hsl(var(--foreground))]/92">
                          <Quote className="w-3.5 h-3.5 inline mr-1 text-[hsl(var(--lime))]/60 align-text-top" />
                          {data.ctaLead}
                        </p>
                        <div className="font-serif-tc text-[18px] sm:text-[22px] lg:text-[24px] leading-[1.35] text-[hsl(var(--foreground))] font-bold">
                          <span>{data.ctaPunch}</span>
                          <br />
                          <span className="lime-keyword">{data.ctaPunchHi}</span>
                        </div>
                      </motion.div>
                    </ResultBlock>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Footer disclaimer */}
          <div className="px-5 sm:px-7 py-3 border-t border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
              * 以上為預錄範例。分享會現場 Jacky 會以你的實際行業、產品、受眾即場生成，深度遠超此展示。
            </p>
            <Button
              data-testid="webinar-cta-ai-demo-button"
              onClick={onCtaClick}
              className="btn-lime h-9 px-4 text-[12px] whitespace-nowrap"
            >
              預約現場生成
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const ResultBlock = ({ icon, label, children, accentRow }) => (
  <div
    className={`rounded-xl p-4 sm:p-5 border ${
      accentRow
        ? "border-[rgba(212,180,106,0.28)] bg-[rgba(212,180,106,0.05)]"
        : "border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)]"
    }`}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--lime))] mb-2.5">
      {icon}
      <span>{label}</span>
    </div>
    {children}
  </div>
);
